import { Circle, CircleCheck, CircleX } from "lucide-mui";
import ToggleButton from "@mui/material/ToggleButton";
import ToggleButtonGroup from "@mui/material/ToggleButtonGroup";
import Tooltip from "@mui/material/Tooltip";
import { cloneElement, type ReactElement, type ReactNode } from "react";

/** A reviewed thing's decision: taken (accepted / disqualified) or still open. */
export type ReviewState = "accepted" | "pending" | "disqualified";

export type ReviewColor = "success" | "warning" | "error" | "info";

/** Accepted is green, undecided amber, disqualified red, unless a caller's option says otherwise. */
export const REVIEW_COLORS: Record<ReviewState, ReviewColor> = {
  accepted: "success",
  pending: "warning",
  disqualified: "error",
};

/** A toggle button in one review colour: outlined in it, filled with it while selected. */
export function reviewToggleSx(color: ReviewColor) {
  return {
    color: `${color}.main`,
    "&.Mui-selected": {
      color: `${color}.contrastText`,
      bgcolor: `${color}.main`,
      "&:hover": { bgcolor: `${color}.dark` },
    },
  };
}

export interface ReviewOption {
  /** Tooltip and aria-label. */
  label: string;
  icon: ReactNode;
  /** In place of the state's `REVIEW_COLORS` colour, to match a chip it sits next to. */
  color?: ReviewColor;
}

const DEFAULT_OPTIONS: Record<ReviewState, ReviewOption> = {
  accepted: { label: "Accept", icon: <CircleCheck fontSize="small" /> },
  pending: { label: "Pending", icon: <Circle fontSize="small" /> },
  disqualified: { label: "Disqualify", icon: <CircleX fontSize="small" /> },
};

const ORDER: ReviewState[] = ["accepted", "pending", "disqualified"];

interface ReviewToggleProps<T extends ReviewState> {
  value: T;
  onChange: (next: T | null) => void;
  disabled?: boolean;
  /** Per-state label and icon, in place of Accept / Pending / Disqualify. */
  options?: Partial<Record<ReviewState, ReviewOption>>;
  /**
   * One more button right after the accept one (e.g. "Pursue & snooze"). It
   * sits in the group for its look, so it sets its own `selected` and keeps the
   * group's `onChange` out of its clicks. Not a Fragment: the group rejects those.
   */
  afterAccepted?: ReactElement;
}

/** Accept / pending / disqualify: the one control for deciding on a reviewed row. */
export default function ReviewToggle<T extends ReviewState>({
  value,
  onChange,
  disabled,
  options,
  afterAccepted,
}: ReviewToggleProps<T>) {
  return (
    <ToggleButtonGroup
      size="small"
      exclusive
      value={value}
      disabled={disabled}
      onChange={(_, next: T | null) => onChange(next)}
    >
      {ORDER.flatMap((state) => {
        const { label, icon, color } = options?.[state] ?? DEFAULT_OPTIONS[state];
        return [
          <Tooltip key={state} title={disabled ? "" : label}>
            <ToggleButton value={state} aria-label={label} sx={reviewToggleSx(color ?? REVIEW_COLORS[state])}>
              {icon}
            </ToggleButton>
          </Tooltip>,
          state === "accepted" && afterAccepted && cloneElement(afterAccepted, { key: "after-accepted" }),
        ];
      })}
    </ToggleButtonGroup>
  );
}
